/**
 * Configuration module for accessing runtime environment variables
 *
 * This module provides a typed interface to access configuration values
 * that are injected at runtime via the window.ENV object.
 *
 * Fallback to import.meta.env is provided for development/build-time scenarios.
 */

import { RuntimeConfig } from './env';

class Config {
  private validateRequired(value: string | undefined, name: string): string {
    if (!value || value === '' || value.startsWith('${')) {
      throw new Error(
        `Required configuration ${name} is missing. ` +
        'Please ensure environment variables are properly configured.'
      );
    }
    return value;
  }

  private getEnv(key: keyof RuntimeConfig, required: boolean = true): string {
    // Try window.ENV first (runtime config)
    const runtimeValue = window.ENV?.[key];

    // Fall back to import.meta.env (build-time config for development)
    const buildTimeValue = import.meta.env[key];

    const value = runtimeValue || buildTimeValue;

    if (required) {
      return this.validateRequired(value, key);
    }

    return value || '';
  }

  private getEnvAsNumber(key: keyof RuntimeConfig, defaultValue: number): number {
    const value = this.getEnv(key, false);
    const parsed = parseInt(value, 10);
    return isNaN(parsed) ? defaultValue : parsed;
  }

  private getEnvAsBoolean(key: keyof RuntimeConfig, defaultValue: boolean): boolean {
    const value = this.getEnv(key, false);
    if (value === '') return defaultValue;
    return value.toLowerCase() === 'true';
  }

  // API Configuration
  get apiUrl(): string {
    return this.validateRequired(this.getEnv('VITE_API_URL'), 'VITE_API_URL');
  }

  // Authentication Configuration
  get keycloakUrl(): string {
    return this.validateRequired(this.getEnv('VITE_KEYCLOAK_URL'), 'VITE_KEYCLOAK_URL');
  }

  get clientId(): string {
    return this.getEnv('VITE_CLIENT_ID', false) || 'hinata-pal-frontend';
  }

  get redirectUri(): string {
    return this.getEnv('VITE_REDIRECT_URI', false) || window.location.origin + '/callback';
  }

  get logoutUri(): string {
    return this.getEnv('VITE_LOGOUT_URI', false) || window.location.origin;
  }

  // Helper to determine if we're in development mode
  private isDevelopment(): boolean {
    return import.meta.env.DEV ||
           this.getEnv('VITE_ENVIRONMENT', false) === 'development';
  }

  // Admin Tools Configuration
  // Returns localhost URLs for local dev (allowing the cards to show)
  // Returns null for production (admin tools not exposed)
  get keycloakAdminUrl(): string | null {
    const url = this.getEnv('VITE_KEYCLOAK_ADMIN_URL', false);
    return url || (this.isDevelopment() ? 'http://localhost:8080' : null);
  }

  get grafanaUrl(): string | null {
    const url = this.getEnv('VITE_GRAFANA_URL', false);
    return url || (this.isDevelopment() ? 'http://localhost:3300' : null);
  }

  get jaegerUrl(): string | null {
    const url = this.getEnv('VITE_JAEGER_URL', false);
    return url || (this.isDevelopment() ? 'http://localhost:16686' : null);
  }

  get kibanaUrl(): string | null {
    const url = this.getEnv('VITE_KIBANA_URL', false);
    return url || (this.isDevelopment() ? 'http://localhost:5601' : null);
  }

  get prometheusUrl(): string | null {
    const url = this.getEnv('VITE_PROMETHEUS_URL', false);
    return url || (this.isDevelopment() ? 'http://localhost:9090' : null);
  }

  get kafkaUiUrl(): string | null {
    const url = this.getEnv('VITE_KAFKA_UI_URL', false);
    return url || (this.isDevelopment() ? 'http://localhost:8090' : null);
  }

  // Application Configuration
  get version(): string {
    return this.getEnv('VITE_VERSION', false) || '1.0.0';
  }

  get logLevel(): string {
    return this.getEnv('VITE_LOG_LEVEL', false) || 'info';
  }

  // Timeout Configuration
  get apiTimeout(): number {
    return this.getEnvAsNumber('VITE_API_TIMEOUT', 30000);
  }

  get tokenRefreshThreshold(): number {
    return this.getEnvAsNumber('VITE_TOKEN_REFRESH_THRESHOLD', 120);
  }

  // Feature Flags
  get enableAnalytics(): boolean {
    return this.getEnvAsBoolean('VITE_ENABLE_ANALYTICS', false);
  }

  get enableDebug(): boolean {
    return this.getEnvAsBoolean('VITE_ENABLE_DEBUG', false);
  }

  // Storage Strategy
  get useSessionStorage(): boolean {
    return this.getEnvAsBoolean('VITE_USE_SESSION_STORAGE', false);
  }
}

// Export singleton instance
const config = new Config();
export default config;
